import { useState } from 'react';

import type { DailySalesReport } from '../../../../api/daily-sales';
import { KpiStrip, SegmentedControl } from '../../primitives';
import { BucketError } from '../../regional/BucketStates';
import {
  formatCount,
  formatMoney,
  formatMoneyAxis,
  summaryCells,
  windowSum,
} from '../daily-sales-format';

import { DailySalesChart } from './DailySalesChart';
import { DailySalesFreshness } from './DailySalesFreshness';
import { DailySalesLedger } from './DailySalesLedger';
import { DailySalesNotes } from './DailySalesNotes';

type RevenueBasis = 'grossCents' | 'subtotalCents';

const REVENUE_BASES = [
  { value: 'grossCents', label: 'Gross payment' },
  { value: 'subtotalCents', label: 'Pre-tax' },
] as const;

const WAREHOUSE_DOWN = new Error(
  'FareHarbor numbers are unavailable right now because the data warehouse could not be read. Nebari numbers are current.',
);

export function DailySalesBody({ report }: { report: DailySalesReport }) {
  const [basis, setBasis] = useState<RevenueBasis>('grossCents');
  const warehouseDown = report.days.some((d) => d.fareharbor === null);
  const shared = { days: report.days, today: report.today };

  return (
    <>
      <DailySalesFreshness report={report} />
      {warehouseDown && <BucketError error={WAREHOUSE_DOWN} />}
      <div className="nb-ds-kpis">
        <KpiStrip items={summaryCells(report)} />
      </div>
      <DailySalesChart
        {...shared}
        title="Bookings per day"
        sources={['fareharbor', 'giftCards', 'nebari']}
        measure="bookings"
        format={formatCount}
        aside={
          <span className="nb-ds-meta">
            {formatCount(windowSum(report.days, 'bookings'))} bookings
          </span>
        }
      />
      <DailySalesChart
        {...shared}
        title="Tickets per day"
        sources={['fareharbor', 'nebari']}
        measure="tickets"
        format={formatCount}
        aside={
          <span className="nb-ds-meta">
            {formatCount(windowSum(report.days, 'tickets', ['fareharbor', 'nebari']))} tickets
          </span>
        }
      />
      <DailySalesChart
        {...shared}
        title="Revenue per day"
        sources={['fareharbor', 'giftCards', 'nebari']}
        measure={basis}
        format={formatMoney}
        axisFormat={formatMoneyAxis}
        aside={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span className="nb-ds-meta">
              {formatMoney(windowSum(report.days, basis))}{' '}
              {basis === 'grossCents' ? 'gross' : 'pre-tax'}
            </span>
            <SegmentedControl
              value={basis}
              onChange={setBasis}
              options={REVENUE_BASES}
              ariaLabel="Revenue basis"
            />
          </div>
        }
      />
      <DailySalesLedger {...shared} />
      <DailySalesNotes />
    </>
  );
}
